/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { uploadBrandLogo } from '@/lib/rxTemplates';
import api from '@/lib/api';

const MAX_BYTES = 1024 * 1024;
const ALLOWED_MIMES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

const fileToDataUri = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

/**
 * LogoUploader — drag&drop + click-to-select uploader for the clinic logo.
 *
 * Wraps `POST /api/admin/brand-settings/logo`. Shows an inline preview and
 * lets the admin remove the logo (which sends logoUrl=null via PUT).
 */
export const LogoUploader = ({ currentUrl, onUploaded }) => {
  const [preview, setPreview] = useState(currentUrl || null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setPreview(currentUrl || null); }, [currentUrl]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!ALLOWED_MIMES.includes(file.type)) {
      toast.error(`Formato non supportato (${file.type || 'sconosciuto'}). Usa PNG, JPEG, WebP o SVG.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File troppo grande (${Math.round(file.size / 1024)} KB). Max 1024 KB.`);
      return;
    }
    setBusy(true);
    try {
      const dataUri = await fileToDataUri(file);
      const updated = await uploadBrandLogo(dataUri);
      setPreview(updated.logoUrl);
      if (typeof onUploaded === 'function') onUploaded(updated);
      toast.success(`Logo caricato (${Math.round(file.size / 1024)} KB)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore durante l\'upload');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Rimuovere il logo attuale?')) return;
    setBusy(true);
    try {
      const updated = await api.put('/admin/brand-settings', { logoUrl: '' });
      setPreview(null);
      if (typeof onUploaded === 'function') onUploaded(updated?.data || {});
      toast.success('Logo rimosso');
    } catch (e) {
      toast.error('Errore rimozione');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="logo-uploader">
      <Label className="text-xs">Logo clinica</Label>
      <div
        role="button"
        tabIndex={0}
        aria-label="Carica logo clinica"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer?.files?.[0];
          if (file) handleFile(file);
        }}
        className={`flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 bg-muted/30'}`}
        data-testid="logo-drop-zone"
      >
        {preview ? (
          <img
            src={preview}
            alt="Logo clinica"
            className="h-16 w-16 flex-shrink-0 rounded-lg object-cover ring-1 ring-border"
            data-testid="logo-preview"
          />
        ) : (
          <div className="h-16 w-16 flex-shrink-0 flex items-center justify-center rounded-lg bg-muted text-muted-foreground" data-testid="logo-placeholder">
            <ImageIcon className="h-8 w-8" />
          </div>
        )}
        <div className="flex-1 min-w-[140px]">
          <div className="text-sm font-medium leading-snug">
            {busy ? 'Caricamento...' : (preview ? 'Trascina per sostituire' : 'Trascina qui o clicca per selezionare')}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            PNG / JPEG / WebP / SVG — max 1024 KB
          </div>
        </div>
        <Input
          ref={inputRef}
          type="file"
          accept={ALLOWED_MIMES.join(',')}
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid="logo-file-input"
        />
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }} data-testid="logo-select-btn">
          <Upload className="h-4 w-4 mr-1" /> Sfoglia
        </Button>
        {preview && (
          <Button type="button" variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(); }} aria-label="Rimuovi logo" data-testid="logo-remove-btn">
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default LogoUploader;
